import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { defer, map, startWith, takeUntil, tap } from 'rxjs';

import { CompositeFormDirective, RmsCompositeFormSubmissionDirective } from '../../../sdk/forms/composite-form';
import { ControlErrorComponent } from '../../../sdk/forms/error';
import { duplicatesValidator } from './email-chips-input.validator';

export interface EmailChip {
  id: string;
  value: string;
  label: string;
}

@Component({
  selector: 'rms-email-chips-input',
  imports: [
    MatFormField,
    MatInputModule,
    MatChipsModule,
    MatIcon,
    ReactiveFormsModule,
    RmsCompositeFormSubmissionDirective,
    ControlErrorComponent,
    TranslateModule,
    MatAutocompleteModule,
    MatOption,
    AsyncPipe,
    MatSelect,
    MatTooltip,
    NgClass,
  ],
  templateUrl: './email-chips-input.component.html',
  styleUrl: './email-chips-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailChipsInputComponent extends CompositeFormDirective<string[]> implements OnInit {
  @Input() public label: string | null = null;
  @Input() public errorLabel = 'libs.email-chips-input.error-label';
  @Input() public placeholder = 'libs.email-chips-input.placeholder';
  @Input() public note: string | null = null;
  @Input() public maxAttendees?: number;
  @Input() public displayInsertSelect = false;
  @Input() public suggestions: EmailChip[] = [];

  private readonly translateService = inject(TranslateService);
  protected separatorKeysCodes: number[] = [COMMA, ENTER, SEMICOLON];

  protected chipsForm = this.fb.group({
    emails: this.fb.control<EmailChip[]>([]),
    input: this.fb.control<string>('', [Validators.email]),
  });

  protected form = this.fb.control<string[]>(
    [],
    [duplicatesValidator, (control) => this.maxAttendeesValidator(control)]
  );

  protected autoCompleteSuggestions$ = defer(() =>
    this.chipsForm.valueChanges.pipe(
      startWith(this.chipsForm.value),
      map((values) => ({ emails: values.emails ?? [], input: values.input ?? '' })),
      map((values) => {
        const selectedSet = new Set(values.emails.map((e) => e.value.toLowerCase()));
        const search = values.input?.toLowerCase().trim();

        return this.suggestions.reduce<{ value: string; label: string }[]>((result, suggestion) => {
          const value = suggestion.value.toLowerCase();
          const label = suggestion.label.toLowerCase();

          if (!selectedSet.has(value) && (!search || value.includes(search) || label.includes(search))) {
            result.push(suggestion);
          }

          return result;
        }, []);
      })
    )
  );

  ngOnInit(): void {
    super.ngOnInit();

    this.chipsForm.controls.emails.valueChanges
      .pipe(
        map((emails) => (emails ? emails.map((email) => email.value) : [])),
        tap((emails) => this.form.patchValue(emails)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  override writeValue(value: string[] | null): void {
    if (!value || !value.length) {
      return;
    }

    this.form.patchValue(value);

    const emails = value.map((email) => ({
      id: crypto.randomUUID(),
      value: email,
      label: this.suggestions.find((suggestion) => suggestion.value === email)?.label ?? email,
    }));

    this.chipsForm.controls.emails.patchValue(emails);
  }

  override setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.chipsForm.enabled && this.chipsForm.disable({ emitEvent: false })
      : this.chipsForm.disabled && this.chipsForm.enable({ emitEvent: false });
  }

  protected get emails(): EmailChip[] {
    return this.chipsForm.controls.emails.value ?? [];
  }

  protected set emails(emails: EmailChip[]) {
    this.chipsForm.controls.emails.patchValue(emails);
  }

  protected add(event: MatChipInputEvent): void {
    if (this.chipsForm.controls.input.invalid) {
      return;
    }

    const value = (event.value || '').trim();

    if (!value) {
      return;
    }

    this.emails = [...this.emails, { id: crypto.randomUUID(), value, label: value }];

    event.chipInput.clear();
    this.chipsForm.controls.input.patchValue('');
  }

  protected insertSelectSuggestion(event: MatSelectChange): void {
    if (!event.value || !event.value.length) {
      return;
    }

    this.insert(event.value);

    event.source.options.forEach((option) => option.deselect());
  }

  protected insertAutocompleteSuggestion(event: MatAutocompleteSelectedEvent): void {
    this.insert(event.option.value);

    event.option.deselect();
  }

  protected remove(id: string): void {
    this.emails = [...this.emails].filter((email) => email.id !== id);
  }

  protected isVariable(value: string): boolean {
    return value.includes('{{') && value.includes('}}');
  }

  private insert(value: string): void {
    const label = this.suggestions.find((email) => email.value === value)?.label;

    this.emails = [...this.emails, { id: crypto.randomUUID(), value, label: label ?? value }];

    this.chipsForm.controls.input.patchValue('');
  }

  private maxAttendeesValidator(control: AbstractControl<string[]>): ValidationErrors | null {
    if (!this.maxAttendees || !Array.isArray(control.value)) {
      return null;
    }

    if (control.value.length > this.maxAttendees) {
      const translationKey =
        this.maxAttendees === 1 ? 'libs.validation.max-attendees.singular' : 'libs.validation.max-attendees.plural';
      const translatedMessage = this.translateService.instant(translationKey, {
        max_size: this.maxAttendees,
      });

      return {
        maxAttendees: translatedMessage,
      };
    }

    return null;
  }
}
